import React, { Component } from "react";
import crystal from "../images/crystal.png";
import logFetchError from "../Functions/LogFetchError";
import "./Gamification.css";

// ─── Crystal SVG ─────────────────────────────────────────────────────────────
const CrystalIcon = ({ size = 20 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    className="gamificationCrystalSvg"
  >
    <polygon
      points="12,2 20,8 17,20 7,20 4,8"
      fill="var(--gamification-crystal-fill)"
      stroke="var(--gamification-crystal-stroke)"
      strokeWidth="1.5"
    />
    <polygon
      points="12,2 20,8 12,6"
      fill="var(--gamification-crystal-top)"
      opacity="0.6"
    />
    <line
      x1="12"
      y1="2"
      x2="12"
      y2="20"
      stroke="var(--gamification-crystal-stroke)"
      strokeWidth="0.75"
      opacity="0.4"
    />
  </svg>
);

// ─── Stat badge ───────────────────────────────────────────────────────────────
const StatBadge = ({ label, value, icon }) => (
  <div className="gamificationStatBadge">
    <span className="gamificationStatBadgeIcon">{icon}</span>
    <span className="gamificationStatBadgeValue">{value}</span>
    <span className="gamificationStatBadgeLabel">{label}</span>
  </div>
);

// ─── Skeleton loader ──────────────────────────────────────────────────────────
const Skeleton = ({ className = "" }) => (
  <div className={`gamificationSkeleton ${className}`} />
);

// ─── Formatting helpers ───────────────────────────────────────────────────────
const formatDate = (dateString) => {
  if (!dateString) return "";
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return dateString;
  return date.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const relativeTime = (isoString) => {
  if (!isoString) return "";
  const date = new Date(isoString);
  if (isNaN(date.getTime())) return isoString;
  const diff = (Date.now() - date.getTime()) / 1000;
  if (diff < 60) return "Just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 172800) return "Yesterday";
  return `${Math.floor(diff / 86400)}d ago`;
};

// ─── Main page ────────────────────────────────────────────────────────────────
export default class Gamification extends Component {
  constructor(props) {
    super(props);
    this.state = {
      statsCollapsed: false,
      gamifyData: null,
      isLoading: true,
      isError: false,
    };
  }

  componentDidMount() {
    this.fetchGamificationData();
  }

  // Fetch from the real endpoint; re-fetches every time the component mounts
  // so study streak is always up to date.
  fetchGamificationData = async () => {
    this.setState({ isLoading: true, isError: false });
    await fetch(`${this.props.route}/getgamificationdata.webapi`, {
      method: "POST",
      headers: {
        Token: this.props.userData.Token,
        "Content-Type": "text/plain",
      },
      body: JSON.stringify({
        CustomerId: this.props.userData.CustomerId,
      }),
    })
      .then((response) => {
        if (response.status === 401) {
          if (this.props.setAuthenticationError) {
            this.props.setAuthenticationError();
          }
          throw new Error("Authentication Error");
        } else if (response.ok) {
          return response.text();
        } else {
          throw new Error("Error fetching gamification data");
        }
      })
      .then((response) => {
        // The endpoint returns the gamification object directly (no wrapper key)
        let result = JSON.parse(response);
        this.setState({
          gamifyData: result,
          isLoading: false,
          isError: false,
        });
      })
      .catch((error) => {
        console.log("error", error);
        this.setState({ isLoading: false, isError: true });
        logFetchError(
          error,
          this.props.userProfile,
          this.props.userData,
          "fetchGamificationData",
        );
      });
  };

  toggleStatsCollapsed = () => {
    this.setState({ statsCollapsed: !this.state.statsCollapsed });
  };

  render() {
    const { gamifyData, isLoading, isError, statsCollapsed } = this.state;

    // ── Loading state ──────────────────────────────────────────────────────
    if (isLoading) {
      return (
        <div className="gamificationBoard">
          <div className="gamificationBoardHeader">
            <span>Gamification</span>
          </div>
          <div className="gamificationBoardContent">
            <div className="gamificationStatsBanner">
              <Skeleton className="gamificationSkeletonBanner" />
            </div>
            {[1, 2].map((i) => (
              <div className="gamificationSection" key={i}>
                <Skeleton className="gamificationSkeletonTitle" />
                <Skeleton className="gamificationSkeletonBody" />
              </div>
            ))}
          </div>
        </div>
      );
    }

    // ── Error state ──────────────────────────────────────────────────────────
    if (isError || !gamifyData) {
      return (
        <div className="gamificationBoard">
          <div className="gamificationBoardHeader">
            <span>Gamification</span>
          </div>
          <div className="gamificationBoardContent">
            <div className="gamificationSection">
              <p className="gamificationEmptyState">
                Could not load gamification data. Please try again later.
              </p>
              <button
                className="gamificationShowMoreBtn"
                onClick={this.fetchGamificationData}
              >
                Retry
              </button>
            </div>
          </div>
        </div>
      );
    }

    // ── Disabled state ─────────────────────────────────────────────────────
    if (gamifyData.gamification_enabled === false) {
      return (
        <div className="gamificationBoard">
          <div className="gamificationBoardHeader">
            <span>Gamification</span>
          </div>
          <div className="gamificationBoardContent">
            <div className="gamificationSection">
              <p className="gamificationEmptyState">
                Gamification is currently disabled for your account.
              </p>
            </div>
          </div>
        </div>
      );
    }

    // ── Derived values ──────────────────────────────────────────────────────
    const {
      crystals,
      crystalsToNextLevel,
      crystalsInCurrentLevel,
      level,
      intelligence,
      wisdom,
      streaks,
      titles,
      recentActivity,
    } = gamifyData;

    // Progress within the current level, driven directly by the API's
    // reported crystalsInCurrentLevel / crystalsToNextLevel values.
    const crystalsNeededLevel = crystalsToNextLevel ?? 1000;
    const crystalsInLevel = crystalsInCurrentLevel ?? 0;
    const progressPct =
      crystalsNeededLevel > 0
        ? Math.min((crystalsInLevel / crystalsNeededLevel) * 100, 100)
        : 0;

    const streakList = streaks ?? [];
    const titleList = titles ?? [];
    const earnedTitles = titleList.filter((t) => t.earned);
    const activityLog = recentActivity ?? [];

    // ── Render ───────────────────────────────────────────────────────────────
    return (
      <div className="gamificationBoard">
        {/* ── Header ─────────────────────────────────────────────────── */}
        <div className="gamificationBoardHeader">
          <span>Gamification</span>
        </div>

        <div className="gamificationBoardContent">
          {/* ── Collapsible stats banner ───────────────────────────────── */}
          <div className="gamificationStatsBanner">
            <button
              className="gamificationStatsBannerToggle"
              onClick={this.toggleStatsCollapsed}
            >
              <span>Your Stats</span>
            </button>

            {!statsCollapsed && (
              <div className="gamificationStatsBannerBody">
                <div className="gamificationCrystalHero">
                  <div className="gamificationCrystalHeroImageContainer">
                    <img src={crystal} alt="Intelligence Crystal" />
                  </div>
                  <div className="gamificationCrystalHeroText">
                    <span className="gamificationCrystalCount">
                      {crystals?.toLocaleString() ?? 0}
                    </span>
                    <span className="gamificationCrystalLabel">
                      Intelligence Crystals
                    </span>
                  </div>
                </div>

                <div className="gamificationStatBadges">
                  <StatBadge label="Level" value={level ?? 1} icon="🏅" />
                  <StatBadge
                    label="Intelligence"
                    value={`+${intelligence ?? 0}`}
                    icon="🧠"
                  />
                  <StatBadge
                    label="Wisdom"
                    value={`+${wisdom ?? 0}`}
                    icon="📚"
                  />
                  <StatBadge
                    label="Titles"
                    value={`${earnedTitles.length}/${titleList.length}`}
                    icon="👑"
                  />
                </div>
              </div>
            )}
          </div>

          {/* ── Level progress ─────────────────────────────────────────── */}
          <div className="gamificationSection">
            <h2 className="gamificationSectionTitle">Level Progress</h2>
            <div className="gamificationLevelCard">
              <div className="gamificationLevelInfo">
                <span className="gamificationLevelCurrent">
                  Level {level ?? 1}
                </span>
                <span className="gamificationLevelNext">
                  Level {(level ?? 1) + 1}
                </span>
              </div>
              <div className="gamificationProgressTrack">
                <div
                  className="gamificationProgressFill"
                  style={{ width: `${progressPct}%` }}
                />
              </div>
              <div className="gamificationLevelSubtext">
                <CrystalIcon size={13} />
                <span>
                  {crystalsInLevel.toLocaleString()} /{" "}
                  {crystalsNeededLevel.toLocaleString()}
                </span>
                <span className="gamificationLevelPct">
                  {Math.round(progressPct)}%
                </span>
              </div>
            </div>
          </div>

          {/* ── Streaks ────────────────────────────────────────────────── */}
          <div className="gamificationSection">
            <h2 className="gamificationSectionTitle">Streaks</h2>
            {streakList.length === 0 ? (
              <p className="gamificationEmptyState">No streak data yet.</p>
            ) : (
              <div className="gamificationStreakGrid">
                {streakList.map((streak, i) => (
                  <div
                    className="gamificationStreakCard"
                    key={streak.label ?? i}
                  >
                    <div className="gamificationStreakIconWrap">
                      {streak.icon ?? "🔥"}
                    </div>
                    <div className="gamificationStreakBody">
                      <span className="gamificationStreakLabel">
                        {streak.label}
                      </span>
                      {streak.description && (
                        <span className="gamificationStreakDescription">
                          {streak.description}
                        </span>
                      )}
                      <div className="gamificationStreakCount">
                        <span className="gamificationStreakValue">
                          {streak.value ?? 0}
                        </span>
                        <span className="gamificationStreakUnit">
                          {streak.unit ?? ""}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* ── Titles ─────────────────────────────────────────────────── */}
          <div className="gamificationSection">
            <h2 className="gamificationSectionTitle">
              Titles
              <span className="gamificationSectionCount">
                {earnedTitles.length} / {titleList.length} earned
              </span>
            </h2>
            {earnedTitles.length === 0 ? (
              <p className="gamificationEmptyState">
                No titles earned yet — complete a case to get started!
              </p>
            ) : (
              <div className="gamificationTitlesGrid">
                {earnedTitles.map((title, i) => (
                  <div
                    className="gamificationTitleCard gamificationTitleCardEarned"
                    key={title.title_id ?? i}
                    title={title.description}
                  >
                    <span className="gamificationTitleBadgeIcon">👑</span>
                    <div className="gamificationTitleBody">
                      <span className="gamificationTitleText">
                        {title.title}
                      </span>
                      {title.category && (
                        <span className="gamificationTitleCategory">
                          {title.category}
                        </span>
                      )}
                      {title.description && (
                        <span className="gamificationTitleCategory">
                          {title.description}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* ── Activity Log ───────────────────────────────────────────── */}
          <div className="gamificationSection">
            <h2 className="gamificationSectionTitle">Activity Log</h2>
            {activityLog.length === 0 ? (
              <p className="gamificationEmptyState">
                No activity yet — complete a case to get started!
              </p>
            ) : (
              <div className="gamificationActivityTable">
                <div className="gamificationActivityTableHead">
                  <span>Event</span>
                  <span>Crystals</span>
                  <span>Time</span>
                </div>
                <ul className="gamificationActivityList">
                  {activityLog.map((item, i) => (
                    <li className="gamificationActivityItem" key={i}>
                      <span className="gamificationActivityDescription">
                        {item.Description ?? item.description ?? ""}
                      </span>
                      <span className="gamificationActivityCrystals">
                        {(item.CrystalsEarned ?? item.crystalsEarned ?? 0) >
                          0 && (
                          <>
                            <CrystalIcon size={12} />+
                            {item.CrystalsEarned ?? item.crystalsEarned}
                          </>
                        )}
                      </span>
                      <span className="gamificationActivityTimestamp">
                        {relativeTime(item.Timestamp ?? item.timestamp)}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }
}
